import {
  DEFAULT_SUBJECT_CATEGORIES,
  SUBJECT_CATEGORY_CODES,
  type SubjectCategoryCode,
} from '@athena/shared'
import { useQuery } from '@apollo/client/react'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { MeDocument } from '../../graphql/generated/operations.js'

const CHANGE_EVENT = 'athena:subject-preferences'
export const subjectPreferencesKey = (userId: string) => `athena:practice-subjects:v1:${userId}`

export function readHiddenSubjects(raw: string | null): string[] {
  try {
    const parsed = raw ? JSON.parse(raw) : null
    const value: unknown = Array.isArray(parsed) ? parsed : parsed?.hiddenIds
    if (!Array.isArray(value) || !value.every((id) => typeof id === 'string')) return []
    return [...new Set(value)]
  } catch {
    return []
  }
}

export function readSubjectCategories(raw: string | null): SubjectCategoryCode[] {
  try {
    const value: unknown = raw ? JSON.parse(raw)?.categoryCodes : undefined
    if (Array.isArray(value) && value.every((code) => SUBJECT_CATEGORY_CODES.includes(code)))
      return [...new Set(value)] as SubjectCategoryCode[]
  } catch {
    /* Use the initial school categories if storage is unavailable or malformed. */
  }
  return [...DEFAULT_SUBJECT_CATEGORIES]
}

export function subjectMatchesCategories(
  subject: { categoryCodes?: readonly SubjectCategoryCode[] },
  categories: readonly SubjectCategoryCode[],
): boolean {
  return (
    !subject.categoryCodes?.length ||
    subject.categoryCodes.some((code) => categories.includes(code))
  )
}

/** Browser/account preferences: categories opt in and hidden IDs opt individual subjects out. */
export function useSubjectPreferences() {
  const { data, loading, error } = useQuery(MeDocument)
  const userId = data?.me?.userId
  const key = userId ? subjectPreferencesKey(userId) : null
  const subscribe = useCallback((notify: () => void) => {
    window.addEventListener('storage', notify)
    window.addEventListener(CHANGE_EVENT, notify)
    return () => {
      window.removeEventListener('storage', notify)
      window.removeEventListener(CHANGE_EVENT, notify)
    }
  }, [])
  const snapshot = useCallback(() => {
    try {
      return key ? window.localStorage.getItem(key) : null
    } catch {
      return null
    }
  }, [key])
  const raw = useSyncExternalStore(subscribe, snapshot, () => null)
  const hiddenIds = useMemo(() => readHiddenSubjects(raw), [raw])
  const categoryCodes = useMemo(() => readSubjectCategories(raw), [raw])
  const save = useCallback(
    (ids: readonly string[], categories: readonly SubjectCategoryCode[] = categoryCodes) => {
      if (!key) return false
      try {
        window.localStorage.setItem(
          key,
          JSON.stringify({ hiddenIds: [...new Set(ids)], categoryCodes: [...new Set(categories)] }),
        )
        window.dispatchEvent(new Event(CHANGE_EVENT))
        return true
      } catch {
        return false
      }
    },
    [key, categoryCodes],
  )
  return { userId, hiddenIds, categoryCodes, save, loading, error }
}
